import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../api";
import { PageTitle, Badge } from "./AdminLayout";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const TONES = { Added: "green", Changed: "cyan", Fixed: "amber", Removed: "red" };
const EMPTY = { version: "", title: "", date: new Date().toISOString().slice(0, 10), published: true };

export default function ChangelogAdmin() {
  const [entries, setEntries] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [changesText, setChangesText] = useState("");

  const load = () => api.get("/admin/changelog").then((r) => setEntries(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openEdit = (e) => {
    setEditing(e);
    setForm({ version: e.version, title: e.title, date: e.date, published: e.published });
    setChangesText(e.changes.map((c) => `${c.type}: ${c.text}`).join("\n"));
  };

  const parseChanges = () => changesText.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
    const m = l.match(/^(Added|Changed|Fixed|Removed)\s*[:\-]\s*(.+)$/i);
    return m ? { type: m[1][0].toUpperCase() + m[1].slice(1).toLowerCase(), text: m[2] } : { type: "Changed", text: l };
  });

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") {
        await api.post("/admin/changelog", { ...form });
        const list = await api.get("/admin/changelog");
        const created = list.data.find((x) => x.version === form.version);
        if (created) await api.patch(`/admin/changelog/${created.id}`, { changes: parseChanges() });
        toast.success(`${form.version} released`);
      } else {
        await api.patch(`/admin/changelog/${editing.id}`, { ...form, changes: parseChanges() });
        toast.success("Update saved");
      }
      setEditing(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete ${e.version}?`)) return;
    await api.delete(`/admin/changelog/${e.id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-changelog">
      <PageTitle title="Changelog" sub="Versioned updates, shown publicly on /changelog.">
        <button onClick={() => { setEditing("new"); setForm(EMPTY); setChangesText("Added: \nFixed: "); }} data-testid="changelog-create-open"
          className="inline-flex items-center gap-2 bg-blurple hover:bg-[#4752c4] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-150">
          <Plus size={15} /> New release
        </button>
      </PageTitle>

      <div className="space-y-3" data-testid="changelog-list">
        {entries.map((e) => (
          <div key={e.id} className="rounded-lg border border-white/5 bg-panel p-5" data-testid={`changelog-row-${e.version}`}>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <span className="font-mono text-volt font-semibold">{e.version}</span>
                <span className="font-heading font-bold text-white">{e.title}</span>
                <Badge tone={e.published ? "green" : "amber"}>{e.published ? "live" : "scheduled/draft"}</Badge>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-mono text-white/30">{e.date}</span>
                <button onClick={() => openEdit(e)} className="p-2 text-white/40 hover:text-white transition-colors duration-150" data-testid={`changelog-edit-${e.version}`} aria-label="Edit"><Pencil size={14} /></button>
                <button onClick={() => remove(e)} className="p-2 text-white/40 hover:text-red-400 transition-colors duration-150" data-testid={`changelog-delete-${e.version}`} aria-label="Delete"><Trash2 size={14} /></button>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {e.changes.map((c, i) => (
                <span key={i} className="text-xs text-white/50 border border-white/10 rounded-full px-3 py-1">
                  <span className={`font-mono mr-1.5 ${c.type === "Added" ? "text-emerald-400" : c.type === "Fixed" ? "text-amber-400" : c.type === "Removed" ? "text-red-400" : "text-volt"}`}>{c.type}</span>
                  {c.text}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="bg-panel border-white/10 text-white max-w-2xl" data-testid="changelog-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing === "new" ? "New release" : `Edit ${form.version}`}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2">
            <div className="grid grid-cols-3 gap-4">
              <input value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} required placeholder="v2.5.0"
                className="bg-white/5 border border-white/10 rounded-md px-4 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blurple" data-testid="changelog-version-input" />
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required
                className="bg-white/5 border border-white/10 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blurple [color-scheme:dark]" data-testid="changelog-date-input" />
              <label className="flex items-center gap-2 text-sm text-white/70">
                <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="accent-[#5865F2] w-4 h-4" data-testid="changelog-published-toggle" /> Published
              </label>
            </div>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required placeholder="Release title"
              className="w-full bg-white/5 border border-white/10 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blurple" data-testid="changelog-title-input" />
            <div>
              <p className="text-xs text-white/40 mb-1.5 font-mono">One change per line — "Added: …", "Changed: …", "Fixed: …", "Removed: …"</p>
              <textarea value={changesText} onChange={(e) => setChangesText(e.target.value)} rows={7}
                className="w-full bg-black/40 border border-white/10 rounded-md px-4 py-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blurple resize-y scanlines" data-testid="changelog-changes-input" />
            </div>
            <button type="submit" className="w-full bg-blurple hover:bg-[#4752c4] text-white font-semibold py-3 rounded-full transition-colors duration-150" data-testid="changelog-save">Save release</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
